import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaPlus, FaMapLocationDot } from "react-icons/fa6";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { Header } from "../../../../components/Header";
import { handleDetectGPS } from "../../../../utilities/leftjs/handleDetectGps";
import { FiCrosshair, FiLoader, FiTrash2 } from "react-icons/fi";
import { getLocationDetail } from "../../../../utilities/leftjs/getLocationDetail";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { InputDefault } from "../../../../components/forms/InputDefault";
import { TextAreaDefault } from "../../../../components/forms/TextAreaDefault";
import { OdcService } from "../../../../services/OdcService";
import { MapSync } from "../../../../utilities/leftjs/MapSync";
import { MapController } from "../../../../utilities/leftjs/MapController";
import { SelectOlt } from "../../../../components/forms/selects/SelectOlt";
import { SelectCentralOdc } from "../../../../components/forms/selects/SelectCentralOdc";

const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

// Lokasi default sebagai fallback
const DEFAULT_CENTER = { lat: -6.17511, lng: 106.827153 };

export const OdcCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingGps, setLoadingGps] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [gpsError, setGpsError] = useState(null);

  const [data, setData] = useState({
    pop_id: "",
    nas_id: "",
    olt_id: "",
    central_odc_id: "",
    main_port: null,
    name: "",
    port: null,
    description: "",
    longitude: DEFAULT_CENTER.lng,
    latitude: DEFAULT_CENTER.lat,
    address: "",
    images: [],
  });

  // State untuk string koordinat gabungan
  const [gpsInput, setGpsInput] = useState(
    `${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`,
  );

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  // Logika perhitungan slot
  const maxImages = 3;
  const currentImages = data?.images?.length || 0;
  const remainingSlots = maxImages - currentImages;

  // Handlers
  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "port") {
      const val = parseInt(value) || 0;
      setData({
        ...data,
        port: val,
        splitter: data.splitter?.slice(0, val) || [],
      });
    } else {
      setData({ ...data, [id]: value });
    }
  };

  // Handler khusus untuk input GPS gabungan
  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);

    // Parse string "lat, lng" menjadi angka
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());

      // Validasi apakah angka yang valid
      if (!isNaN(lat) && !isNaN(lng)) {
        setData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        // Reset error jika user berhasil input manual
        setGpsError(null);
      }
    }
  };

  // Cleanup URL object saat komponen unmount atau file berubah
  useEffect(() => {
    return () => {
      previews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previews]);

  const handleFileChange = (e) => {
    try {
      const files = Array.from(e.target.files || []);

      if (files.length > remainingSlots) {
        alert(`Maksimal unggah ${remainingSlots} foto lagi.`);
        e.target.value = null;
        return;
      }

      const newPreviews = files
        .map((file) => {
          try {
            return URL.createObjectURL(file);
          } catch (err) {
            console.warn("Gagal membuat preview untuk file:", err);
            return null;
          }
        })
        .filter(Boolean);

      setSelectedFiles((prev) => [...prev, ...files].slice(0, remainingSlots));
      setPreviews((prev) => [...prev, ...newPreviews].slice(0, remainingSlots));
      e.target.value = null;
    } catch (error) {
      console.warn("Gagal memproses file:", error);
    }
  };

  const removeSelectedFile = (index) => {
    try {
      if (previews[index]) {
        URL.revokeObjectURL(previews[index]);
      }
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      setPreviews((prev) => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.warn("Gagal menghapus file:", error);
    }
  };

  const handleLocationUpdate = async (lat, lng) => {
    try {
      setData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));

      // Update input GPS string juga
      setGpsInput(`${lat}, ${lng}`);

      // Reset error GPS jika berhasil
      setGpsError(null);

      // Fetch Alamat (Reverse Geocoding)
      setFetchingAddress(true);
      try {
        const locationData = await getLocationDetail(lat, lng);
        if (locationData && locationData.displayName) {
          setData((prev) => ({
            ...prev,
            address: locationData.displayName,
          }));
        }
      } catch (err) {
        console.warn(
          "Gagal mengambil alamat, melanjutkan tanpa alamat otomatis:",
          err,
        );
      } finally {
        setFetchingAddress(false);
      }
    } catch (error) {
      console.warn("Gagal update lokasi:", error);
    }
  };

  // Fungsi untuk deteksi GPS dengan error handling lengkap
  const safeDetectGps = async () => {
    setLoadingGps(true);
    setGpsError(null);

    try {
      await handleDetectGPS({
        setLoading: setLoadingGps,
        handleLocationUpdate: handleLocationUpdate,
      });
    } catch (error) {
      console.warn(
        "GPS tidak tersedia atau gagal, menggunakan lokasi default:",
        error.message || error,
      );

      let errorMessage = "GPS tidak tersedia";
      if (error.code === 1) {
        errorMessage = "Akses lokasi ditolak oleh browser";
      } else if (error.code === 2) {
        errorMessage = "Lokasi tidak tersedia saat ini";
      } else if (error.code === 3) {
        errorMessage = "Waktu habis saat mendeteksi lokasi";
      } else if (error.message?.includes("permission")) {
        errorMessage = "Izin akses lokasi belum diberikan";
      }

      setGpsError(errorMessage);

      setData((prev) => ({
        ...prev,
        latitude: DEFAULT_CENTER.lat,
        longitude: DEFAULT_CENTER.lng,
      }));
      // Update input string juga
      setGpsInput(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
    } finally {
      setLoadingGps(false);
    }
  };

  // Deteksi GPS saat komponen mount
  useEffect(() => {
    safeDetectGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("pop_id", data.pop_id);
      formData.append("nas_id", data.nas_id);
      formData.append("olt_id", data.olt_id);
      formData.append("central_odc_id", data.central_odc_id);
      formData.append("main_port", data.main_port);
      formData.append("name", data.name);
      formData.append("port", data.port);
      formData.append("description", data.description || "");
      formData.append("latitude", data.latitude);
      formData.append("longitude", data.longitude);
      formData.append("address", data.address || "");

      selectedFiles.forEach((file) => {
        formData.append("images[]", file);
      });

      await OdcService.create({ data: formData });
      navigate("/dashboard/master-data/odc");
    } catch (error) {
      alert("Gagal menyimpan data.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tambah Data ODC - DANELA NET</title>
        <meta name="description" content="Tambah data ODC baru." />
      </Helmet>

      <div>
        {/* HEADER */}
        <Header showToggleSidebar={false}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-700/50 rounded-full transition-all text-slate-400 hover:text-white"
            >
              <FaChevronLeft />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">
                Tambah Data ODC
              </h2>
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                Form Input ODC Baru
              </p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* INFORMASI DASAR */}
            <div className="grid grid-cols-1 gap-6">
              <div
                className="lg:col-span-7 rounded-2xl p-5 lg:p-6"
                style={CARD_STYLE}
              >
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                  Informasi Dasar
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SelectPop
                        label="Pop"
                        placeholder="Silahkan pilih Pop"
                        nas_id={data.nas_id}
                        value={data.pop_id}
                        setValue={(selectedId) =>
                          setData((prev) => ({ ...prev, pop_id: selectedId }))
                        }
                        searchable={true}
                        disabled={data.nas_id === ""}
                      />

                      <SelectNas
                        label="Nas Server - MikroTik"
                        placeholder="Silahkan pilih Nas Server"
                        value={data.nas_id}
                        setValue={(selectedId) =>
                          setData((prev) => ({ ...prev, nas_id: selectedId }))
                        }
                        searchable={true}
                      />

                      <SelectOlt
                        label="Olt"
                        placeholder="Silahkan pilih Olt"
                        value={data.olt_id}
                        setValue={(selectedId) =>
                          setData((prev) => ({ ...prev, olt_id: selectedId }))
                        }
                        searchable={true}
                      />

                      <SelectCentralOdc
                        label="Central ODC"
                        placeholder="Silahkan pilih Central ODC"
                        value={data.central_odc_id}
                        setValue={(selectedId) =>
                          setData((prev) => ({
                            ...prev,
                            central_odc_id: selectedId,
                          }))
                        }
                        searchable={true}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputDefault
                        id="name"
                        label="Nama ODC"
                        type="text"
                        placeholder="Masukkan nama ODC..."
                        value={data.name}
                        onChange={handleChange}
                        required
                      />

                      <InputDefault
                        id="mainport"
                        label="Port Utama"
                        type="number"
                        placeholder="Masukkan port utama..."
                        inputMode={"numeric"}
                        value={data.main_port}
                        onChange={(e) =>
                          setData((prev) => ({ ...prev, main_port: e.target.value }))
                        }
                        required
                      />
                      <InputDefault
                        id="port"
                        label="Jumlah Port"
                        type="number"
                        placeholder="Masukkan jumlah port..."
                        inputMode={"numeric"}
                        value={data.port}
                        onChange={(e) =>
                          setData((prev) => ({ ...prev, port: e.target.value }))
                        }
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <TextAreaDefault
                      id="description"
                      label="Deskripsi"
                      placeholder="Masukkan deskripsi..."
                      value={data.description}
                      onChange={handleChange}
                      rows={5}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Image & Location */}
            <div
              className="rounded-2xl p-1 border border-slate-700/50"
              style={{ background: "rgba(15, 23, 42, 0.5)" }}
            >
              <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                  <FaMapLocationDot className="text-cyan-500" /> Peta Lokasi
                </h3>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-500">
                    Lat:{" "}
                    <span className="text-cyan-400">
                      {data.latitude.toFixed(5)}
                    </span>
                  </span>
                  <span className="text-slate-500">
                    Lng:{" "}
                    <span className="text-cyan-400">
                      {data.longitude.toFixed(5)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-2">
                <div className="">
                  {/* Image */}
                  <div>
                    {previews.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase">
                          Akan Diunggah ({previews.length})
                        </label>
                        <div className="grid grid-cols-3 gap-2 p-2">
                          {previews.map((url, index) => (
                            <div
                              key={index}
                              className="relative group rounded-lg overflow-hidden border border-gray-800 aspect-square shadow-inner"
                            >
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Hapus"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {previews.length < 3 && (
                      <label
                        htmlFor="file-upload"
                        className="w-full border-2 border-dashed border-slate-600 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-700/30 hover:border-cyan-500/50 transition-all group mt-5 gap-3 py-2"
                      >
                        <FaPlus className="text-slate-500 group-hover:text-cyan-400 transition-colors" />
                        <span className="text-[9px] font-black text-slate-400 uppercase">
                          Upload Image
                        </span>
                        <input
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                          disabled={selectedFiles.length >= remainingSlots}
                        />
                      </label>
                    )}
                  </div>

                  <div className="mt-5 flex flex-col gap-3">
                    {/* ============================================ */}
                    {/* INPUT GPS GABUNGAN - FORMAT: lat, lng */}
                    {/* ============================================ */}
                    <div className="relative">
                      <label
                        htmlFor="gps-input"
                        className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5"
                      >
                        GPS
                      </label>
                      <div className="relative">
                        <input
                          id="gps-input"
                          type="text"
                          inputMode="decimal"
                          placeholder="-6.17511, 106.827153"
                          value={gpsInput}
                          onChange={handleGpsInputChange}
                          className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-20"
                          required
                        />
                        <button
                          type="button"
                          onClick={safeDetectGps}
                          disabled={loadingGps}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
                          title="Deteksi GPS"
                        >
                          {loadingGps ? (
                            <FiLoader className="animate-spin text-sm" />
                          ) : (
                            <FiCrosshair className="text-sm" />
                          )}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">
                        Format: latitude, longitude
                      </p>
                    </div>

                    <TextAreaDefault
                      id="address"
                      label="Detail Alamat"
                      placeholder="Alamat akan terisi otomatis..."
                      value={data.address}
                      onChange={handleChange}
                    />

                    {/* Pesan Error GPS (jika ada) */}
                    {gpsError && (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <span className="text-amber-400 text-[11px] leading-relaxed">
                          ⚠️ {gpsError} — Menggunakan lokasi default. Klik peta
                          atau ikon crosshair untuk mengubah.
                        </span>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 mt-1">
                      * Klik peta untuk mengisi otomatis.
                    </p>
                  </div>
                </div>

                <div className="min-h-[400px] w-full relative z-0 rounded-xl overflow-clip">
                <MapContainer
                   center={[data.latitude, data.longitude]}
                   zoom={15}
                   scrollWheelZoom={true}
                   className="w-full h-full"
                 >
                   <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                   <MapController data={data} setData={setData} />
                 </MapContainer>
                </div>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-300 border border-slate-600 hover:bg-slate-800 transition-all uppercase tracking-widest"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-cyan-900/20 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Menyimpan..." : "Simpan Data"}
              </button>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};
